import { useLoyaltyContext } from '~/shared/hooks/loyalty';
import { createLoyaltyQuery } from '~/services/loyalty/lib';
import { useQuery } from '@tanstack/react-query';
import { QueryKeys } from '~/services/loyalty/constants';
import type {
    LoyaltyApiError,
    MemberAccountInformationResponse,
} from '~/lib/data-contract/loyalty';
import type { AllowedOptions } from '~/shared/hooks/loyalty/allowedQueryOptions';

/**
 * Wrapper around useMemberAccountInformation that suppresses error toasts
 * Used in my-profile to prevent error toasts when Business Central API is unavailable
 */
export const useMemberAccountInformationSilent = <TData = MemberAccountInformationResponse>(
    options?: AllowedOptions<MemberAccountInformationResponse, TData>,
) => {
    const { businessCentralId, translate, trackError, trackOnlyError } = useLoyaltyContext();

    return useQuery<MemberAccountInformationResponse, LoyaltyApiError, TData>({
        queryKey: [QueryKeys.MEMBER_ACCOUNT_INFORMATION],
        queryFn: createLoyaltyQuery(
            'GetMemberAccountInformation',
            translate,
            trackError,
            {
                memberId: businessCentralId,
            },
            {
                suppressErrorToast: true,
                trackOnlyError,
            },
        ),
        enabled: false,
        refetchOnWindowFocus: false,
        retry: false,
        gcTime: 1000 * 30,
        ...options,
    });
};
